import { useEffect, useRef, useState } from 'react';
import { useGlobalStore } from '@digiwedge/global-state';
import { readEnvVar } from '../../env.runtime.js';

export type CanInput = { feature: string; action: string; tenantId?: string };
export type CapabilityReason = 'explicit' | 'bypass' | 'none' | undefined;
export type CanResult = {
  allowed: boolean;
  loading: boolean;
  error?: string;
  reason?: CapabilityReason;
};

export function useCan(input: CanInput): CanResult {
  const feature = input.feature;
  const action = input.action;
  const tenantId = input.tenantId;
  const token = useGlobalStore((state) => state.token);
  const isAuthenticated = useGlobalStore((state) => state.isAuthenticated);
  const key = `${feature}:${action}:${tenantId ?? ''}`;
  const authScope = token ? `bearer:${token}` : isAuthenticated ? 'cookie' : 'anonymous';
  const cacheKey = `${key}:${authScope}`;

  const cacheRef = useRef(new Map<string, { allowed: boolean; reason?: CapabilityReason }>());
  const [state, setState] = useState<CanResult>({ allowed: false, loading: true, reason: 'none' });

  useEffect(() => {
    let cancelled = false;
    if (!isAuthenticated) {
      setState({ allowed: false, loading: false, reason: 'none' });
      return;
    }

    const cached = cacheRef.current.get(cacheKey);
    if (cached !== undefined) {
      setState({ allowed: cached.allowed, loading: false, reason: cached.reason });
      return;
    }
    const run = async () => {
      try {
        const base = readEnvVar('VITE_ACCESS_CONTROL_BASE_URL') ?? '';
        const url = `${String(base).replace(/\/$/, '') || ''}/api/capabilities/can`;
        const requestToken = token;
        const headers: Record<string, string> = { 'content-type': 'application/json' };
        if (requestToken) headers['Authorization'] = `Bearer ${requestToken}`;
        if (tenantId) headers['x-tenant-id'] = tenantId;
        const res = await fetch(url.startsWith('/api/') ? url : url.replace(/^\//, ''), {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify({ checks: [{ feature, action, tenantId }] }),
        });
        if (!res.ok) throw new Error(`capabilities/can HTTP ${res.status}`);
        const data = (await res.json()) as {
          results: Array<{
            feature: string;
            action: string;
            allowed: boolean;
            reason?: 'explicit' | 'bypass' | 'none';
          }>;
        };
        const first = data.results?.[0];
        const allowed = Boolean(first?.allowed);
        const reason = first?.reason ?? undefined;
        cacheRef.current.set(cacheKey, { allowed, reason });
        if (!cancelled) setState({ allowed, loading: false, reason });
      } catch (e: unknown) {
        if (!cancelled)
          setState({
            allowed: false,
            loading: false,
            error: e instanceof Error ? e.message : String(e),
            reason: 'none',
          });
      }
    };
    setState((s) => ({ ...s, loading: true }));
    run();
    return () => {
      cancelled = true;
    };
  }, [cacheKey, feature, action, key, tenantId, isAuthenticated, token]);

  return state;
}
